import type { Agent } from '../stores/chatStore'

const API_BASE = '/api/v1'

export interface StatsResponse {
  total_agents: number
  callable_agents: number
  endpoints_by_protocol: Record<string, number>
}

export interface SearchResponse {
  agents: Agent[]
  total: number
  summary?: string
}

export interface SearchParams {
  q?: string
  protocol?: string
  online_only?: boolean
  callable_only?: boolean
  capability?: string
  limit?: number
  offset?: number
}

export async function fetchStats(): Promise<StatsResponse> {
  const res = await fetch(`${API_BASE}/stats`)
  if (!res.ok) throw new Error('Failed to fetch stats')
  return res.json()
}

export async function searchAgents(params: SearchParams): Promise<SearchResponse> {
  const query = params.q?.trim() || ''

  // For empty queries or very short queries, use GET endpoint (basic text search)
  if (query.length < 2) {
    const searchParams = new URLSearchParams()
    if (params.protocol) searchParams.set('protocol', params.protocol)
    if (params.online_only || params.callable_only) searchParams.set('online_only', 'true')
    if (params.capability) searchParams.set('capability', params.capability)
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.offset) searchParams.set('offset', String(params.offset))

    const res = await fetch(`${API_BASE}/search?${searchParams}`)
    if (!res.ok) throw new Error('Failed to search agents')
    return res.json()
  }

  // Use POST endpoint with semantic search (hybrid: text + vector)
  const body: {
    query: string
    filters?: {
      protocol?: string[]
      availability?: 'callable' | 'reachable' | 'experimental'
    }
    limit?: number
    offset?: number
  } = {
    query,
    limit: params.limit || 20,
    offset: params.offset || 0,
  }

  // Map filters
  if (params.protocol || params.online_only || params.callable_only) {
    body.filters = {}

    if (params.protocol) {
      body.filters.protocol = [params.protocol]
    }

    if (params.online_only || params.callable_only) {
      body.filters.availability = 'callable'
    }
  }

  const res = await fetch(`${API_BASE}/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) throw new Error('Failed to search agents')
  return res.json()
}

export async function getAgent(agentKey: string): Promise<Agent> {
  const res = await fetch(`${API_BASE}/agents/${encodeURIComponent(agentKey)}`)
  if (!res.ok) throw new Error('Failed to fetch agent')
  return res.json()
}

export interface FeedbackItem {
  client: string
  score: number
  tag1: string | null
  tag2: string | null
  comment: string | null
  timestamp: number | null // Unix timestamp in milliseconds
  is_revoked: boolean
  transaction_url: string | null
  block_number: number | null
  response: {
    comment: string | null
    responder: string | null
    timestamp: number | null
    transaction_url: string | null
  } | null
}

/**
 * Convert a timestamp to relative time string (e.g., "2 hours ago", "3 months ago")
 */
export function formatRelativeTime(timestamp: number | null): string | null {
  if (!timestamp) return null

  const now = Date.now()
  const diff = now - timestamp
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (years > 0) return years === 1 ? '1 year ago' : `${years} years ago`
  if (months > 0) return months === 1 ? '1 month ago' : `${months} months ago`
  if (days > 0) return days === 1 ? '1 day ago' : `${days} days ago`
  if (hours > 0) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
  if (minutes > 0) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`
  return 'just now'
}

export interface FeedbackRating {
  score: number
  unique_clients: number
  total_feedback: number
}

export interface FeedbackResponse {
  feedback: FeedbackItem[]
  total: number
  rating: FeedbackRating
}

export async function getAgentFeedback(agentKey: string): Promise<FeedbackResponse> {
  const res = await fetch(`${API_BASE}/agents/${encodeURIComponent(agentKey)}/feedback`)
  if (!res.ok) throw new Error('Failed to fetch feedback')
  return res.json()
}

// =============================================================================
// Agent Capabilities (from DB)
// =============================================================================

export interface DBCapability {
  name: string
  type: 'mcp_tool' | 'a2a_skill' | 'oasf_skill' | 'oasf_domain' | 'general'
  description: string | null
  source: 'metadata_parsed' | 'probed' | null
  raw_data: Record<string, unknown> | null
  requires_payment: boolean | null  // null = unknown, true = 402, false = free
  last_probed_at: string | null
  endpoint_url: string | null
  endpoint_protocol: string | null
}

export interface CapabilitiesResponse {
  capabilities: DBCapability[]
  total: number
}

export async function getAgentCapabilities(agentKey: string): Promise<CapabilitiesResponse> {
  const res = await fetch(`${API_BASE}/agents/${encodeURIComponent(agentKey)}/capabilities`)
  if (!res.ok) throw new Error('Failed to fetch capabilities')
  return res.json()
}

// =============================================================================
// MCP Tool Testing
// =============================================================================

export interface MCPTool {
  name: string;
  description?: string;
  inputSchema?: {
    type: string;
    properties?: Record<string, {
      type: string;
      description?: string;
    }>;
    required?: string[];
  };
}

export interface MCPToolsResponse {
  success: boolean;
  tools: MCPTool[];
  endpoint: string;
  error?: string;
}

export interface MCPCallResponse {
  success: boolean;
  result?: unknown;
  error?: unknown;
  payment_required?: boolean;
  auth_required?: boolean;
  payment_info?: unknown;
  status?: number;
  latency_ms?: number;
}

export async function fetchMCPTools(endpointUrl: string): Promise<MCPToolsResponse> {
  const res = await fetch(`${API_BASE}/mcp/tools`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint_url: endpointUrl }),
  });
  return res.json();
}

export async function callMCPTool(
  endpointUrl: string,
  toolName: string,
  args: Record<string, unknown>
): Promise<MCPCallResponse> {
  const res = await fetch(`${API_BASE}/mcp/call`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint_url: endpointUrl,
      tool_name: toolName,
      arguments: args,
    }),
  });
  return res.json();
}

// =============================================================================
// A2A Agent Card
// =============================================================================

export interface A2ASkill {
  id?: string;
  name: string;
  description?: string;
  tags?: string[];
  examples?: string[];
  inputModes?: string[];
  outputModes?: string[];
  [key: string]: unknown;  // Allow additional fields
}

export interface A2AAgentCardResponse {
  success: boolean;
  agent_card?: Record<string, unknown>;
  skills: A2ASkill[];
  endpoint: string;
  messaging_url?: string;  // The actual URL to send messages to (from agent_card.url)
  messaging_url_warning?: string;  // Warning if URL looks like a website, not a service endpoint
  requires_payment?: boolean;  // True if endpoint returns 402 (x402 payment required)
  payment_info?: Record<string, unknown>;  // 402 response body with pricing details
  preferred_transport?: string;  // How to call the endpoint: jsonrpc, rest, grpc, etc.
  error?: string;
}

export async function fetchA2AAgentCard(endpointUrl: string): Promise<A2AAgentCardResponse> {
  const res = await fetch(`${API_BASE}/a2a/agent-card`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint_url: endpointUrl }),
  });
  return res.json();
}

// =============================================================================
// Domain Verification (ERC-8004)
// =============================================================================

export interface DomainVerificationResponse {
  success: boolean;
  verified: boolean;
  reason: 'same_domain' | 'registration_match' | 'no_registration_file' | 'invalid_format' | 'no_matching_registration' | 'fetch_failed';
  message: string;
  registration?: {
    agentRegistry?: string;
    agentId?: string | number;
  };
  found_registrations?: number;
  error?: string;
}

export async function verifyDomain(
  endpointUrl: string,
  agentKey: string,       // e.g., "1:0x8004...:123" for saving to DB
  agentRegistry: string,  // e.g., "erc8004:1:0x8004..."
  agentId: string,
  agentUri?: string
): Promise<DomainVerificationResponse> {
  const res = await fetch(`${API_BASE}/verify-domain`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint_url: endpointUrl,
      agent_key: agentKey,
      agent_registry: agentRegistry,
      agent_id: agentId,
      agent_uri: agentUri,
    }),
  });
  return res.json();
}

// =============================================================================
// Featured Capabilities (Homepage)
// =============================================================================

export interface FeaturedCapability {
  name: string;
  description: string | null;
  requiresPayment: boolean;
  agentCount: number;
  agents: Array<{
    key: string;
    name: string | null;
    image: string | null;
    availability: string;
  }>;
}

export interface FeaturedCapabilitiesResponse {
  mcp_tools: FeaturedCapability[];
  a2a_skills: FeaturedCapability[];
  stats: {
    total_registered: number;
    free_capabilities: number;
    paid_capabilities: number;
    agents_with_working_capabilities: number;
  };
}

export async function getFeaturedCapabilities(): Promise<FeaturedCapabilitiesResponse> {
  const res = await fetch(`${API_BASE}/featured-capabilities`);
  if (!res.ok) throw new Error('Failed to fetch featured capabilities');
  return res.json();
}

// =============================================================================
// Helpers
// =============================================================================

export function generateSearchSummary(agents: Agent[], query: string, total?: number): string {
  const count = total ?? agents.length

  if (count === 0) {
    return `No agents found matching "${query}". Try broadening your search or checking for different protocols.`
  }

  const callableCount = agents.filter(a => a.availability === 'callable').length
  const protocols = new Set(
    agents.flatMap(a =>
      a.endpoints?.map(e => (e as any).protocol || e.protocol_type) || []
    ).filter(Boolean)
  )
  const protocolList = Array.from(protocols)
    .filter(Boolean)
    .map(p => p === 'http_unknown' ? 'HTTP' : p.toUpperCase())
    .join(', ')

  let summary = `Found ${count} agent${count > 1 ? 's' : ''}`

  if (query) {
    summary += ` matching "${query}"`
  }

  summary += `. ${callableCount} callable.`

  if (protocols.size > 0) {
    summary += ` Available via ${protocolList}.`
  }

  // Highlight top agent if available
  if (agents.length > 0 && agents[0].name) {
    const topAgent = agents[0]
    if (topAgent.availability === 'callable') {
      summary += ` ${topAgent.name} is recommended with verified endpoints.`
    }
  }

  return summary
}
